/**
 * Agency-only session shell bootstrap (2026-09-07).
 *
 * The app shell — which sidebar renders (`UI.theme.type`), where static
 * assets live (`app.staticURL`), the merchant favicon — is only ever
 * hydrated by `initResponseStateUpdate()` after a SELLER-authenticated
 * `seller-init`. An invite-only employee never holds a seller JWT and
 * `fetchAppInit` deliberately no-ops for them (hotfix 2026-08-06), so their
 * session stayed on the legacy theme-1 shell: the old sidebar without
 * Brands / Reporting / the brand switcher, and a broken avatar because
 * `staticURL` was ''. That is the "broken UI, no brands" clients reported.
 *
 * `seller-init` also answers WITHOUT a bearer (the documented "without an
 * access token" scenario) and still carries `app_data.static_base_url`.
 * We take just that, and flip the theme the same way the seller path does
 * (`state-updates.ts`: `if (static_base_url) { setStaticURL; setAppThemeType('2') }`).
 * Nothing else from the payload is applied — no shop / profile / subscription
 * state, which for these sessions is owned by `/api/v1/me` (App.tsx mirror).
 */
import store from '../../store/store';
import { setStaticURL } from '../../store/reducers/appSlice';
import { setAppThemeType } from '../../store/reducers/uiSlice';
import { INIT_API } from '../constants/central-api';

export const AGENCY_SHELL_THEME = '2';

export interface AgencyShellInitResult {
  staticBaseUrl: string | null;
}

/**
 * Fetch the unauthenticated init payload and apply the shell bits.
 * Never throws — a failure leaves the shell as it was (theme 1), which is
 * the pre-fix behaviour, not a worse one.
 */
export async function bootstrapAgencyShell(
  fetchImpl: typeof fetch = fetch
): Promise<AgencyShellInitResult> {
  try {
    const response = await fetchImpl(INIT_API, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    const body: any = await response.json();
    const staticBaseUrl: unknown = body?.data?.app_data?.static_base_url;

    if (typeof staticBaseUrl === 'string' && staticBaseUrl.length > 0) {
      store.dispatch(setStaticURL(staticBaseUrl));
      store.dispatch(setAppThemeType(AGENCY_SHELL_THEME));
      return { staticBaseUrl };
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[agency shell] seller-init bootstrap failed', err);
  }
  return { staticBaseUrl: null };
}

/**
 * True when the session is agency-only: an `emp_` agency key is present but
 * no seller JWT, and we are not on a white-label host. Mirrors the guard the
 * App.tsx `/me` → `all_shops` mirror uses.
 */
export function isAgencyOnlySession(
  agencyAccessToken: string | undefined | null,
  sellerToken: string | undefined | null,
  isWhiteLabel: boolean
): boolean {
  return Boolean(agencyAccessToken) && !sellerToken && !isWhiteLabel;
}
